import { useState, useEffect, useRef, useId, type KeyboardEvent, type ChangeEvent } from 'react';
import styles from './ComboBox.module.css';

interface ComboBoxProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  error?: string;
  required?: boolean;
}

export function ComboBox({ label, value, onChange, suggestions, placeholder, error, required }: ComboBoxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Only filter when the user is actively typing a search query
  const filtered = isSearching && inputValue
    ? suggestions.filter((s) => s.toLowerCase().includes(inputValue.toLowerCase()))
    : suggestions;

  // Show "Use [typed value]" only when actively searching and input doesn't match any suggestion
  const exactMatch = suggestions.some((s) => s.toLowerCase() === inputValue.toLowerCase());
  const showUseCustom = isSearching && inputValue.trim() && !exactMatch;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setIsSearching(false);
        // Commit the value on blur
        if (inputValue.trim()) {
          onChange(inputValue.trim());
        }
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, inputValue, onChange]);

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setInputValue(val);
    setIsSearching(true);
    setHighlightIndex(-1);
    if (!open) setOpen(true);
  }

  function selectValue(val: string) {
    setInputValue(val);
    onChange(val);
    setOpen(false);
    setIsSearching(false);
    setHighlightIndex(-1);
  }

  function handleKeyDown(e: KeyboardEvent) {
    const totalItems = filtered.length + (showUseCustom ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else {
          setHighlightIndex((prev) => Math.min(prev + 1, totalItems - 1));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) {
          setHighlightIndex((prev) => Math.max(prev - 1, 0));
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (open && highlightIndex >= 0) {
          if (showUseCustom && highlightIndex === 0) {
            selectValue(inputValue.trim());
          } else {
            const idx = showUseCustom ? highlightIndex - 1 : highlightIndex;
            if (idx >= 0 && idx < filtered.length) {
              selectValue(filtered[idx]);
            }
          }
        } else if (inputValue.trim()) {
          selectValue(inputValue.trim());
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
          setIsSearching(false);
        }
        break;
      case 'Tab':
        if (open) {
          setOpen(false);
          setIsSearching(false);
          if (inputValue.trim()) {
            onChange(inputValue.trim());
          }
        }
        break;
    }
  }

  const activeDescendant =
    open && highlightIndex >= 0 ? `${listboxId}-option-${highlightIndex}` : undefined;

  return (
    <div className={styles.field}>
      {label && <label className={styles.label}>{label}</label>}
      <div ref={wrapRef} className={styles.comboWrap}>
        <input
          ref={inputRef}
          type="text"
          className={`${styles.input} ${error ? styles.inputError : ''}`}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
          autoComplete="off"
        />

        {open && (filtered.length > 0 || showUseCustom) && (
          <div className={styles.panel} role="listbox" id={listboxId}>
            {showUseCustom && (
              <button
                id={`${listboxId}-option-0`}
                type="button"
                role="option"
                aria-selected={highlightIndex === 0}
                className={`${styles.option} ${styles.optionCustom} ${highlightIndex === 0 ? styles.optionHighlight : ''}`}
                onClick={() => selectValue(inputValue.trim())}
                onMouseEnter={() => setHighlightIndex(0)}
              >
                Use &ldquo;{inputValue.trim()}&rdquo;
              </button>
            )}
            {filtered.map((suggestion, idx) => {
              const optIdx = showUseCustom ? idx + 1 : idx;
              const isSelected = suggestion.toLowerCase() === value.toLowerCase();
              return (
                <button
                  key={suggestion}
                  id={`${listboxId}-option-${optIdx}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`${styles.option} ${isSelected ? styles.optionActive : ''} ${optIdx === highlightIndex ? styles.optionHighlight : ''}`}
                  onClick={() => selectValue(suggestion)}
                  onMouseEnter={() => setHighlightIndex(optIdx)}
                >
                  {suggestion}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
}
